import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AntarcticaPolarMap from "../components/AntarcticaMap";
import TokenCard from "../components/TokenCard";
import MintModal from "../components/MintModal";
import { fetchTokens, fetchSaleStatus, fetchTopTokens } from "../lib/api";
import { useNavigate } from "react-router-dom";

export default function ExplorerPage() {
    const { t } = useTranslation();
    const [phase, setPhase] = useState("early_access");
    const [page, setPage] = useState(1);
    const [tokens, setTokens] = useState([]);
    const [status, setStatus] = useState(null);
    const [tops, setTops] = useState([]);
    const [mintToken, setMintToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, st, topList] = await Promise.all([
                fetchTokens({ phase, page, page_size: 24 }),
                fetchSaleStatus(),
                fetchTopTokens(),
            ]);
            setTokens(list);
            setStatus(st);
            setTops(topList);
        } catch (err) {
            console.error("Explorer load failed:", err);
        } finally {
            setLoading(false);
        }
    }, [phase, page]);

    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="explorer-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
            <div className="text-xs uppercase tracking-[0.3em] text-frost mb-3">
                {t("explorer.kicker")}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
                {t("explorer.title_1")} <span className="italic">{t("explorer.title_2")}</span> {t("explorer.title_3")}
            </h1>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-5">
                    <AntarcticaPolarMap
                        topTokens={tops}
                        height={520}
                        onPickToken={(n) => navigate(`/token/${n}`)}
                    />
                </div>

                <div className="md:col-span-7">
                    <div className="flex flex-wrap gap-2 mb-6">
                        {[
                            { k: "early_access", l: t("explorer.filter_early") },
                            { k: "public_sale", l: t("explorer.filter_public") },
                            { k: "top_token", l: t("explorer.filter_top") },
                        ].map((p) => (
                            <button
                                key={p.k}
                                data-testid={`filter-${p.k}`}
                                onClick={() => { setPhase(p.k); setPage(1); }}
                                className={`btn-sharp px-4 py-2 text-xs border ${
                                    phase === p.k
                                        ? "bg-white text-obsidian border-white"
                                        : "border-white/15 text-slate-400 hover:text-white hover:border-white/40"
                                }`}
                            >
                                {p.l}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="text-center text-slate-500 py-24 font-mono text-xs">
                            {t("explorer.loading")}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {tokens.map((tk) => (
                                <TokenCard key={tk.number} token={tk} onMint={(t) => setMintToken(t)} />
                            ))}
                        </div>
                    )}

                    {phase !== "top_token" && (
                        <div className="mt-8 flex justify-between items-center">
                            <button
                                data-testid="page-prev"
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page === 1}
                                className="btn-sharp border border-white/15 px-4 py-2 text-xs disabled:opacity-30 hover:border-white/40"
                            >
                                ← {t("explorer.prev")}
                            </button>
                            <span className="font-mono text-xs text-slate-500">
                                {t("explorer.page", { n: page })}
                            </span>
                            <button
                                data-testid="page-next"
                                onClick={() => setPage(page + 1)}
                                className="btn-sharp border border-white/15 px-4 py-2 text-xs hover:border-white/40"
                            >
                                {t("explorer.next")} →
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <MintModal
                open={!!mintToken}
                onClose={() => setMintToken(null)}
                token={mintToken}
                status={status}
                onSuccess={() => { setMintToken(null); load(); }}
            />
        </div>
    );
}
